import BarChart from '../../Common/BarChart';
import React, {FC} from 'react';

import {StatNameType, StatData, TeamInfo, StatType} from 'utils/types';

import './styles.scss';

const isStatComplete = (stat: StatData) =>
  stat.type === 'tuple'
    ? stat.home.every(s => s !== null) && stat.away.every(s => s !== null)
    : stat.home !== null && stat.away !== null && !(stat.home === 0 && stat.away === 0);

const getBarWidths = (home: number | null, away: number | null) =>
  home !== null && away !== null && home + away !== 0
    ? [((home / (home + away)) * 100).toFixed(0), ((away / (home + away)) * 100).toFixed(0)]
    : [0, 0];

const tupleStatElement = (tuple: [number | null, number | null]) =>
  tuple[0] !== null && tuple[1] !== null && tuple[1] !== 0 ? (
    <>
      <span className="WidgetStats__statNumber">{((tuple[0] / tuple[1]) * 100).toFixed(0)}%</span>
      <span className="WidgetStats__statAnnotation">
        {tuple[0]} / {tuple[1]}
      </span>
    </>
  ) : null;

const simpleStatElement = (number: number, type: Exclude<StatType, 'tuple'>) => (
  <span className="WidgetStats__statNumber">{`${number}${type === 'percentage' ? '%' : ''}`}</span>
);

const StatsRow = ({stat}: {stat: StatData}) => {
  const [barWidthHome, barWidthAway] =
    stat.type === 'tuple'
      ? getBarWidths(stat.home[1], stat.away[1])
      : getBarWidths(stat.home, stat.away);

  const [statHome, statAway] =
    stat.type === 'tuple'
      ? [tupleStatElement(stat.home), tupleStatElement(stat.away)]
      : [simpleStatElement(stat.home, stat.type), simpleStatElement(stat.away, stat.type)];

  return statHome && statAway ? (
    <div className="WidgetStats__row">
      <div className="WidgetStats__stat WidgetStats__stat--home">{statHome}</div>
      <h4 className="WidgetStats__name">{stat.name}</h4>
      <div className="WidgetStats__stat WidgetStats__stat--away">{statAway}</div>
      <div className="WidgetStats__bars">
        <div
          className={`WidgetStats__bar WidgetStats__bar--home ${
            barWidthAway === '0' ? 'WidgetStats__bar--single' : ''
          }`}
          style={{width: `${barWidthHome}%`}}
        />
        <div
          className={`WidgetStats__bar WidgetStats__bar--away ${
            barWidthHome === '0' ? 'WidgetStats__bar--single' : ''
          }`}
          style={{width: `${barWidthAway}%`}}
        />
      </div>
    </div>
  ) : null;
};

export interface WidgetStatsProps {
  teams: {
    home: TeamInfo;
    away: TeamInfo;
  };
  statsData: StatData[];
}

const WidgetStats: FC<WidgetStatsProps> = ({teams, statsData}: WidgetStatsProps) => (
  <section className="WidgetStats">
    <h3 className="WidgetStats__header">
      <span>{teams.home.name}</span> <span>{teams.away.name}</span>
    </h3>
    {Object.entries(statsData).map(
      ([key, stat]) =>
        stat &&
        isStatComplete(stat) &&
        (stat.key === 'breakpointsWon' ? (
          <div className="WidgetStats__barChart">
            <h4 className="WidgetStats__name">{stat.name}</h4>
            <BarChart home={{value: `${stat.home}`}} away={{value: `${stat.away}`}} />
          </div>
        ) : (
          <StatsRow stat={stat} key={key} />
        )),
    )}
  </section>
);

export default WidgetStats;
